import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ImagePlus, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Drywall", "Painting", "Tile & Marble", "Flooring", "Complete Refresh"];
const BUDGETS = ["Under $2.5k", "$2.5k – $5k", "$5k – $10k", "$10k – $20k", "$20k – $30k", "$30k+"];

const compressImage = (file) =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const max = 1600;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
                (blob) => {
                    URL.revokeObjectURL(url);
                    if (!blob) return resolve(file);
                    resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
                },
                "image/jpeg",
                0.82,
            );
        };
        img.onerror = () => resolve(file);
        img.src = url;
    });

const STEPS = [
    { n: "01", t: "We review your inquiry", d: "Your details and photos come straight to our inbox." },
    { n: "02", t: "On-site walkthrough", d: "We visit, measure and talk through the finish you want." },
    { n: "03", t: "Free detailed estimate", d: "A clear line-item quote — no surprises mid-project." },
];

const inputCls =
    "w-full border border-line bg-bone/60 px-4 py-3.5 text-sm text-espresso placeholder:text-fog/70 transition-colors focus:border-oak focus:bg-white focus:outline-none focus:ring-2 focus:ring-oak/20";
const labelCls = "mb-2 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-clay";

export const InquiryForm = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", projectType: "", description: "" });
    const [budget, setBudget] = useState(2);
    const [photo, setPhoto] = useState(null);
    const [preview, setPreview] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const fileRef = useRef(null);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file (JPG, PNG, WebP).");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error("That photo is very large — please pick one under 10MB.");
            return;
        }
        const compressed = await compressImage(file);
        if (photo) URL.revokeObjectURL(preview);
        setPhoto(compressed);
        setPreview(URL.createObjectURL(compressed));
    };

    const removePhoto = () => {
        if (preview) URL.revokeObjectURL(preview);
        setPhoto(null);
        setPreview(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name);
            fd.append("email", form.email);
            fd.append("phone", form.phone);
            fd.append("project_type", form.projectType);
            fd.append("description", form.description);
            fd.append("budget", BUDGETS[budget]);
            if (photo) fd.append("photo", photo);
            const { data } = await axios.post(`${API}/inquiries`, fd);
            setDone({ email: form.email, name: form.name.split(" ")[0], emailed: data.email_sent !== false });
            toast.success("Inquiry sent — we'll be in touch shortly.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || "Something went wrong — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setForm({ name: "", email: "", phone: "", projectType: "", description: "" });
        setBudget(2);
        removePhoto();
        setDone(null);
    };

    return (
        <section id="inquiry" className="scroll-mt-20 bg-sand/60 py-20 md:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal className="mb-14">
                    <Eyebrow>Start your project</Eyebrow>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="grid grid-cols-1 overflow-hidden bg-white shadow-frame lg:grid-cols-5">
                        {/* Left editorial panel */}
                        <div className="relative overflow-hidden bg-espresso p-8 text-bone md:p-12 lg:col-span-2">
                            <div className="texture-draft absolute inset-0 opacity-20 invert" aria-hidden="true" />
                            <div className="relative flex h-full flex-col">
                                <h2 className="font-serif text-3xl font-light leading-[1.1] tracking-tight md:text-4xl">
                                    Tell us about
                                    <br />
                                    <span className="italic text-wood">your project.</span>
                                </h2>
                                <p className="mt-6 text-sm leading-relaxed text-bone/70">
                                    A few details are all we need to get moving. Attach
                                    a photo of the space and we will arrive at the
                                    walkthrough already knowing the room.
                                </p>

                                <div className="mt-10 flex flex-1 flex-col gap-7 border-t border-bone/10 pt-8">
                                    {STEPS.map((s) => (
                                        <div key={s.n} className="flex gap-5">
                                            <span className="font-mono text-xs text-wood">{s.n}</span>
                                            <div>
                                                <p className="text-sm font-semibold text-bone">{s.t}</p>
                                                <p className="mt-1 text-xs leading-relaxed text-bone/60">{s.d}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.22em] text-bone/50">
                                    Free estimates · Broward &amp; Palm Beach County
                                </p>
                            </div>
                        </div>

                        {/* Form / success panel */}
                        <div className="relative p-8 md:p-12 lg:col-span-3">
                            <AnimatePresence mode="wait">
                                {done ? (
                                    <motion.div
                                        key="success"
                                        data-testid="inquiry-success-panel"
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -24 }}
                                        transition={{ duration: 0.5 }}
                                        className="flex h-full min-h-[420px] flex-col items-center justify-center text-center"
                                    >
                                        <motion.span
                                            initial={{ scale: 0, rotate: -30 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                                            className="flex h-20 w-20 items-center justify-center rounded-full bg-sage/10 text-sage"
                                        >
                                            <CheckCircle2 className="h-10 w-10" />
                                        </motion.span>
                                        <h3 className="mt-8 font-serif text-3xl text-espresso md:text-4xl">
                                            Inquiry received, {done.name}.
                                        </h3>
                                        <p className="mt-4 max-w-md text-sm leading-relaxed text-clay md:text-base">
                                            {done.emailed
                                                ? "Your project details just landed in our inbox. "
                                                : "Your project details were saved and the team has been notified. "}
                                            Our team will contact you within a few hours to discuss your project further.
                                        </p>
                                        <motion.button
                                            data-testid="inquiry-reset-button"
                                            whileHover={{ scale: 1.03 }}
                                            whileTap={{ scale: 0.97 }}
                                            onClick={reset}
                                            className="mt-10 inline-flex items-center gap-2 border border-espresso/20 px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-espresso transition-colors hover:border-espresso hover:bg-espresso hover:text-bone"
                                        >
                                            <RotateCcw className="h-4 w-4" />
                                            Send another inquiry
                                        </motion.button>
                                    </motion.div>
                                ) : (
                                    <motion.form
                                        key="form"
                                        data-testid="inquiry-form"
                                        onSubmit={onSubmit}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, y: -24 }}
                                        transition={{ duration: 0.4 }}
                                        className="flex flex-col gap-6"
                                    >
                                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                            <div>
                                                <label htmlFor="inq-name" className={labelCls}>Full name *</label>
                                                <input
                                                    id="inq-name"
                                                    data-testid="inquiry-name-input"
                                                    required
                                                    maxLength={120}
                                                    value={form.name}
                                                    onChange={set("name")}
                                                    placeholder="Jordan Steevens"
                                                    className={inputCls}
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="inq-email" className={labelCls}>Email *</label>
                                                <input
                                                    id="inq-email"
                                                    data-testid="inquiry-email-input"
                                                    required
                                                    type="email"
                                                    maxLength={200}
                                                    value={form.email}
                                                    onChange={set("email")}
                                                    placeholder="you@email.com"
                                                    className={inputCls}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="inq-phone" className={labelCls}>Phone (optional)</label>
                                            <input
                                                id="inq-phone"
                                                data-testid="inquiry-phone-input"
                                                type="tel"
                                                maxLength={40}
                                                value={form.phone}
                                                onChange={set("phone")}
                                                placeholder="(954) 555-0123"
                                                className={inputCls}
                                            />
                                        </div>

                                        <div>
                                            <span className={labelCls}>Project type *</span>
                                            <div
                                                data-testid="inquiry-project-type-select"
                                                role="radiogroup"
                                                aria-label="Project type"
                                                className="flex flex-wrap gap-2"
                                            >
                                                {PROJECT_TYPES.map((t) => (
                                                    <button
                                                        key={t}
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={form.projectType === t}
                                                        data-testid={`inquiry-type-pill-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                                        onClick={() => setForm((f) => ({ ...f, projectType: t }))}
                                                        className={`border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-all duration-300 ${
                                                            form.projectType === t
                                                                ? "border-oak bg-oak text-bone shadow-lift"
                                                                : "border-line bg-white text-clay hover:border-espresso hover:text-espresso"
                                                        }`}
                                                    >
                                                        {t}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="inq-desc" className={labelCls}>Describe your project *</label>
                                            <textarea
                                                id="inq-desc"
                                                data-testid="inquiry-description-input"
                                                required
                                                maxLength={5000}
                                                rows={5}
                                                value={form.description}
                                                onChange={set("description")}
                                                placeholder="e.g. Primary bathroom — remove old tub, install marble tile in shower, repaint walls warm white, new LVP flooring in bedroom."
                                                className={`${inputCls} resize-y`}
                                            />
                                        </div>

                                        <div>
                                            <div className="flex items-baseline justify-between">
                                                <label htmlFor="inq-budget" className={`${labelCls} mb-0`}>Budget range</label>
                                                <span
                                                    data-testid="inquiry-budget-label"
                                                    className="font-serif text-xl italic text-oak"
                                                >
                                                    {BUDGETS[budget]}
                                                </span>
                                            </div>
                                            <input
                                                id="inq-budget"
                                                data-testid="inquiry-budget-slider"
                                                type="range"
                                                min="0"
                                                max="5"
                                                step="1"
                                                value={budget}
                                                onChange={(e) => setBudget(Number(e.target.value))}
                                                className="budget-slider mt-4"
                                                style={{ "--fill": `${(budget / 5) * 100}%` }}
                                            />
                                            <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-[0.12em] text-fog">
                                                <span>Small refresh</span>
                                                <span>Whole-home</span>
                                            </div>
                                        </div>

                                        <div>
                                            <span className={labelCls}>Add a photo (optional)</span>
                                            <div
                                                data-testid="inquiry-photo-upload"
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    setDragOver(true);
                                                }}
                                                onDragLeave={() => setDragOver(false)}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setDragOver(false);
                                                    handleFile(e.dataTransfer.files?.[0]);
                                                }}
                                                onClick={() => fileRef.current?.click()}
                                                className={`flex cursor-pointer items-center gap-4 border border-dashed p-5 transition-all duration-300 ${
                                                    dragOver
                                                        ? "border-oak bg-oak/5"
                                                        : "border-line bg-bone/40 hover:border-espresso/40"
                                                }`}
                                            >
                                                <input
                                                    ref={fileRef}
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    aria-label="Upload a photo of the space"
                                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                                />
                                                {preview ? (
                                                    <div className="flex w-full items-center gap-4">
                                                        <img
                                                            src={preview}
                                                            alt="Your project space"
                                                            className="h-16 w-16 shrink-0 object-cover"
                                                        />
                                                        <div className="min-w-0 flex-1 text-left">
                                                            <p className="truncate text-sm font-medium text-espresso">{photo?.name}</p>
                                                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-fog">
                                                                {photo ? `${(photo.size / 1024).toFixed(0)} KB` : ""}
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            data-testid="inquiry-photo-remove"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removePhoto();
                                                            }}
                                                            aria-label="Remove photo"
                                                            className="flex h-10 w-10 shrink-0 items-center justify-center border border-line text-clay transition-colors hover:border-red-300 hover:text-red-500"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-4 text-left">
                                                        <span className="flex h-12 w-12 items-center justify-center bg-sand text-oak">
                                                            <ImagePlus className="h-5 w-5" />
                                                        </span>
                                                        <div>
                                                            <p className="text-sm font-medium text-espresso">
                                                                Drop a photo here or click to browse
                                                            </p>
                                                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-fog">
                                                                JPG · PNG · WebP — helps us quote faster
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <motion.button
                                            data-testid="inquiry-submit-button"
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.98 }}
                                            type="submit"
                                            disabled={submitting}
                                            className="mt-2 flex items-center justify-center gap-3 bg-oak px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-bone transition-colors hover:bg-oakdark disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Sending your inquiry…
                                                </>
                                            ) : (
                                                "Send Project Inquiry"
                                            )}
                                        </motion.button>
                                    </motion.form>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
